// overview_manager.jsx
// AutoLey top line and the review queue: who is locked out by the device
// limit right now, which licenses hold the same computer twice, and how many
// activations (and refusals) happened recently.
//
// Dataviz: headline numbers are stat tiles. Activations and refusals are two
// SEPARATE single-series charts in the same hue — different measures, so they
// never share axes, and neither needs a legend (the section title names it).

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Ban, Copy, Gauge, KeyRound, Monitor, RefreshCw, TrendingUp, ShieldAlert, ListChecks, DatabaseZap, CheckCircle2,
} from 'lucide-react';
import { useAutoLey } from '../../authentication_components/autoleyContext';
import {
  Section, StatTile, TrendBars, Loading, EmptyState, Button, LicenseFlags, SlotMeter, controlClass,
  fillDays, fmtInt, fmtDateTime, fmtAgo, accountName,
} from './autoley_ui';
import { LicenseDetailModal } from './licenses_manager';

const WINDOWS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
];

export default function OverviewManager({ onShowLicenses }) {
  const { fetchOverview } = useAutoLey();

  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [unavailable, setUnavailable] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async (windowDays, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      setOverview(await fetchOverview({ days: windowDays }));
      setUnavailable(null);
    } catch (err) {
      if (err.status === 503) setUnavailable(err.message);
      else toast.error(err.message || 'Failed to load the AutoLey overview');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetchOverview]);

  useEffect(() => { load(days); }, [load, days]);

  const refresh = () => { setRefreshing(true); load(days, { silent: true }); };

  if (unavailable) {
    return (
      <div className="mx-auto max-w-3xl px-3 py-10 sm:px-4">
        <EmptyState
          icon={DatabaseZap}
          title="The AutoLey section isn't connected yet"
          hint={`${unavailable}. Set AUTOLEY_DATABASE_URL on the admin backend to the AutoLey license server's database (on Heroku: heroku addons:attach <autoley database> -a <admin app> --as AUTOLEY_DATABASE).`}
          action={<Button variant="secondary" onClick={refresh}>Try again</Button>}
        />
      </div>
    );
  }

  const o = overview;

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-3 py-6 sm:px-4 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-800">AutoLey licenses</h2>
          <p className="text-sm text-slate-500">
            Activations per computer, duplicate installs and lawyers locked out by the device limit.
            {o && <span className="text-slate-400"> Updated {fmtDateTime(o.generated_at)}.</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className={`${controlClass} w-40`}>
            {WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
          <Button variant="secondary" onClick={refresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <StatTile label="Locked out" icon={Ban} loading={loading} value={o?.blocked} tone={o?.blocked ? 'alert' : undefined}
          hint="refused, nothing changed since" />
        <StatTile label="Duplicate installs" icon={Copy} loading={loading} value={o?.licenses_with_duplicates}
          tone={o?.licenses_with_duplicates ? 'warn' : undefined}
          hint={o ? `${fmtInt(o.duplicate_records)} slot${o.duplicate_records === 1 ? '' : 's'} could be freed` : undefined} />
        <StatTile label="At device limit" icon={Gauge} loading={loading} value={o?.at_limit} hint="next activation is refused" />
        <StatTile label="Active licenses" icon={KeyRound} loading={loading} value={o?.active_licenses}
          hint={o ? `${fmtInt(o.revoked_licenses)} revoked · ${fmtInt(o.accounts)} accounts` : undefined} />
        <StatTile label="Active computers" icon={Monitor} loading={loading} value={o?.active_devices} tone="accent" />
        <StatTile label={`Activations (${days}d)`} icon={TrendingUp} loading={loading} value={o?.activations}
          hint={o ? `${fmtInt(o.refused)} refused` : undefined} />
      </div>

      <Section
        title="Needs review"
        subtitle="Locked-out lawyers first, then likely duplicate installs"
        icon={ListChecks}
        right={onShowLicenses && <Button variant="ghost" className="text-xs" onClick={() => onShowLicenses('needs_review')}>Open in Licenses</Button>}
        className="p-0 sm:p-0"
      >
        {loading && !o ? (
          <Loading label="Loading the review queue…" />
        ) : !o || o.needs_review.length === 0 ? (
          <div className="px-5 pb-5">
            <EmptyState icon={CheckCircle2} title="Nothing to review" hint="No lawyer is locked out and no license holds the same computer twice." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-5 py-3">Account</th>
                  <th className="px-5 py-3">Why</th>
                  <th className="px-5 py-3">Computers</th>
                  <th className="px-5 py-3">Last refused</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {o.needs_review.map((l) => (
                  <tr key={l.license_id} className="cursor-pointer hover:bg-blue-50/40" onClick={() => setOpenId(l.license_id)}>
                    <td className="px-5 py-3">
                      <div className="font-semibold text-slate-800">{accountName(l.account)}</div>
                      <div className="text-xs text-slate-400">{l.account?.email} · key …{l.key_hint}</div>
                    </td>
                    <td className="px-5 py-3"><LicenseFlags license={l} /></td>
                    <td className="px-5 py-3"><SlotMeter active={l.active_devices} max={l.max_devices} /></td>
                    <td className="whitespace-nowrap px-5 py-3 text-slate-500">{l.last_refused_at ? fmtAgo(l.last_refused_at) : '—'}</td>
                    <td className="px-5 py-3 text-right"><span className="text-xs font-semibold text-blue-700">Review</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Section title="Activations per day" subtitle="New device records the server created" icon={TrendingUp}>
          {loading && !o ? <Loading /> : <TrendBars points={fillDays(o?.activation_timeline, days)} label="activations" emptyLabel="No activations in this window" />}
        </Section>
        <Section title="Refused activations per day" subtitle="Turned away by the device limit" icon={ShieldAlert}>
          {loading && !o ? <Loading /> : <TrendBars points={fillDays(o?.refused_timeline, days)} label="refused" emptyLabel="No refused activations in this window" />}
        </Section>
      </div>

      <LicenseDetailModal licenseId={openId} onClose={() => setOpenId(null)} onMutated={() => load(days, { silent: true })} />
    </div>
  );
}
